import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared page shell for /admin routes.
 *
 * PageContainer: consistent max-width, vertical rhythm, and the app-wide
 * mount transition (`.page-enter` in index.css — fade + 8px rise, disabled
 * under prefers-reduced-motion). Every admin page root should be one of
 * these instead of an ad-hoc `container mx-auto px-6 py-8` / gradient div.
 *
 * PageHeader: the one canonical page title treatment (there were 15 distinct
 * <h1> class strings across containers before this existed). `actions` is the
 * right-aligned slot for the page's primary buttons.
 */
export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Full-bleed pages (scheduler grids, wide tables) skip the max-w-7xl cap
      but keep the mount animation and vertical rhythm. */
  fullWidth?: boolean;
}

export function PageContainer({
  className,
  fullWidth = false,
  children,
  ...props
}: PageContainerProps) {
  return (
    <div
      className={cn(
        'page-enter mx-auto w-full space-y-6',
        !fullWidth && 'max-w-7xl',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Right-aligned slot for the page's primary actions. */
  actions?: React.ReactNode;
}

export function PageHeader({
  title,
  description,
  actions,
  className,
  children,
  ...props
}: PageHeaderProps) {
  return (
    <div
      className={cn('flex flex-wrap items-start justify-between gap-4', className)}
      {...props}
    >
      <div className="min-w-0 space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      ) : null}
      {children}
    </div>
  );
}
